import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTripById, useUpdateTripRoutingDetails } from "@/api/trips";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { MapPin, Info } from "lucide-react";
import {
  APIProvider,
  Map as GoogleMap,
  Marker,
} from "@vis.gl/react-google-maps";
import {
  AutocompleteAddress,
  GoogleAddress,
} from "@/components/address-auto-complete";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Define LatLngLiteral type based on Google Maps API
interface LatLngLiteral {
  lat: number;
  lng: number;
}

// Updated location structure to include full address details
interface LocationData {
  lat: number;
  lng: number;
  address?:
    | {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      }
    | string; // Add string as possible type for backward compatibility
}

export function RouteSettingsDialog({
  tripId,
  isOpen,
  onOpenChange,
}: {
  tripId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: tripDetails } = useTripById(tripId);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"start" | "end">("start");
  const [mapCenter, setMapCenter] = useState<LatLngLiteral>({
    lat: 43.6532,
    lng: -79.3832,
  }); // Toronto default

  const [useCustomStart, setUseCustomStart] = useState(
    tripDetails?.useCustomStartLocation ?? false
  );
  const [useCustomEnd, setUseCustomEnd] = useState(
    tripDetails?.useCustomEndLocation ?? false
  );

  // Initialize states with properly typed null values
  const [startLocation, setStartLocation] = useState<LocationData | null>(null);
  const [endLocation, setEndLocation] = useState<LocationData | null>(null);

  useEffect(() => {
    if (tripDetails) {
      setUseCustomStart(tripDetails.useCustomStartLocation ?? false);
      setUseCustomEnd(tripDetails.useCustomEndLocation ?? false);

      // Handle startLocation
      try {
        if (
          tripDetails.startLocation &&
          typeof tripDetails.startLocation === "object" &&
          "lat" in tripDetails.startLocation &&
          tripDetails.startLocation.lat != null && // Check for null/undefined
          "lng" in tripDetails.startLocation &&
          tripDetails.startLocation.lng != null // Check for null/undefined
        ) {
          const lat = Number(tripDetails.startLocation.lat);
          const lng = Number(tripDetails.startLocation.lng);

          if (!isNaN(lat) && !isNaN(lng)) {
            const location: LocationData = {
              lat,
              lng,
              address: tripDetails.startLocation
                .address as LocationData["address"], // Assign address directly
            };
            setStartLocation(location);
          } else {
            console.warn(
              "Parsed invalid Lat/Lng from startLocation:",
              tripDetails.startLocation
            );
            setStartLocation(null);
          }
        } else {
          setStartLocation(null);
        }
      } catch (e) {
        setStartLocation(null);
        console.error("Error parsing start location:", e);
      }

      // Handle endLocation
      try {
        if (
          tripDetails.endLocation &&
          typeof tripDetails.endLocation === "object" &&
          "latitude" in tripDetails.endLocation &&
          tripDetails.endLocation.latitude != null && // Check for null/undefined
          "longitude" in tripDetails.endLocation &&
          tripDetails.endLocation.longitude != null // Check for null/undefined
        ) {
          const lat = Number(tripDetails.endLocation.latitude);
          const lng = Number(tripDetails.endLocation.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            const location: LocationData = {
              lat,
              lng,
              address: tripDetails.endLocation
                .address as LocationData["address"], // Assign address directly
            };
            setEndLocation(location);
          } else {
            console.warn(
              "Parsed invalid Lat/Lng from endLocation:",
              tripDetails.endLocation
            );
            setEndLocation(null);
          }
        } else {
          setEndLocation(null);
        }
      } catch (e) {
        setEndLocation(null);
        console.error("Error parsing end location:", e);
      }
    }
  }, [tripDetails]);

  // Update map center when a location is selected
  useEffect(() => {
    if (activeTab === "start" && startLocation) {
      setMapCenter({
        lat: startLocation.lat,
        lng: startLocation.lng,
      });
    } else if (activeTab === "end" && endLocation) {
      setMapCenter({ lat: endLocation.lat, lng: endLocation.lng });
    }
  }, [activeTab, startLocation, endLocation]);

  const updateRoutingDetailsMutation = useUpdateTripRoutingDetails(tripId, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Trip routing details updated successfully",
        variant: "default",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update trip routing details",
        variant: "destructive",
      });
    },
  });

  // Handle address selection from autocomplete
  const handleAddressSelect = (address: GoogleAddress | null) => {
    if (!address?.location) return;

    const location: LocationData = {
      lat: address.location.lat,
      lng: address.location.lng,
      address: {
        line1: address.addressComponents?.line1,
        line2: address.addressComponents?.line2,
        city: address.addressComponents?.city,
        state: address.addressComponents?.state,
        postalCode: address.addressComponents?.postalCode,
        country: address.addressComponents?.country,
      },
    };

    if (activeTab === "start" && useCustomStart) {
      setStartLocation(location);
    } else if (activeTab === "end" && useCustomEnd) {
      setEndLocation(location);
    }
  };

  // Helper to render address display
  const renderAddress = (address: LocationData["address"]) => {
    if (!address) return null;

    // If address is a string, display it directly
    if (typeof address === "string") {
      return <p className="mt-1">{address}</p>;
    }

    // If address is an object with properties, format them
    return (
      <p className="mt-1">
        {[
          address.line1,
          address.line2,
          address.city,
          address.state,
          address.postalCode,
        ]
          .filter(Boolean)
          .join(", ")}
      </p>
    );
  };

  const handleSaveSettings = () => {
    // Prepare data for the API call
    const payload = {
      useCustomStartLocation: useCustomStart,
      useCustomEndLocation: useCustomEnd,
      startLocation: useCustomStart ? startLocation : null,
      endLocation: useCustomEnd ? endLocation : null,
    };

    updateRoutingDetailsMutation.mutate(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Trip Route Settings</DialogTitle>
          <DialogDescription>
            Configure custom start and end locations for route calculation
          </DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue="start"
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "start" | "end")}
        >
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="start">Start Location</TabsTrigger>
            <TabsTrigger value="end">End Location</TabsTrigger>
          </TabsList>

          <TabsContent value="start">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="use-custom-start"
                  className="flex items-center gap-2"
                >
                  <Info className="h-4 w-4" />
                  Use custom start location
                </Label>
                <Switch
                  id="use-custom-start"
                  checked={useCustomStart}
                  onCheckedChange={setUseCustomStart}
                />
              </div>

              {useCustomStart && (
                <>
                  {/* Map Selection */}
                  <div className="h-[250px] w-full relative border rounded-md overflow-hidden">
                    <APIProvider
                      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                    >
                      <GoogleMap
                        // Ensure mapCenter is correctly typed
                        center={mapCenter}
                        defaultZoom={13}
                        gestureHandling="greedy"
                        disableDefaultUI={true}
                      >
                        {startLocation && (
                          <Marker
                            position={{
                              lat: startLocation.lat,
                              lng: startLocation.lng,
                            }}
                          />
                        )}
                      </GoogleMap>
                    </APIProvider>
                  </div>

                  {/* Address Search */}
                  <div className="space-y-2">
                    <Label>Search for address</Label>
                    <AutocompleteAddress
                      mode="search"
                      onAddressChange={handleAddressSelect}
                      placeholder="Enter start location address"
                    />
                  </div>

                  {/* Display selected location details */}
                  {startLocation && (
                    <div className="bg-muted p-3 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <h4 className="font-medium">Selected Start Location</h4>
                      </div>
                      <div className="text-sm">
                        <p>
                          Coordinates: {startLocation.lat.toFixed(6)},{" "}
                          {startLocation.lng.toFixed(6)}
                        </p>
                        {renderAddress(startLocation.address)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="use-custom-end"
                  className="flex items-center gap-2"
                >
                  <Info className="h-4 w-4" />
                  Use custom end location
                </Label>
                <Switch
                  id="use-custom-end"
                  checked={useCustomEnd}
                  onCheckedChange={setUseCustomEnd}
                />
              </div>

              {useCustomEnd && (
                <>
                  {/* Map Selection */}
                  <div className="h-[250px] w-full relative border rounded-md overflow-hidden">
                    <APIProvider
                      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                    >
                      <GoogleMap
                        // Ensure mapCenter is correctly typed
                        center={mapCenter}
                        defaultZoom={13}
                        gestureHandling="greedy"
                        disableDefaultUI={true}
                      >
                        {endLocation && (
                          <Marker
                            position={{
                              lat: endLocation.lat,
                              lng: endLocation.lng,
                            }}
                          />
                        )}
                      </GoogleMap>
                    </APIProvider>
                  </div>

                  {/* Address Search */}
                  <div className="space-y-2">
                    <Label>Search for address</Label>
                    <AutocompleteAddress
                      mode="search"
                      onAddressChange={handleAddressSelect}
                      placeholder="Enter end location address"
                    />
                  </div>

                  {/* Display selected location details */}
                  {endLocation && (
                    <div className="bg-muted p-3 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <h4 className="font-medium">Selected End Location</h4>
                      </div>
                      <div className="text-sm">
                        <p>
                          Coordinates: {endLocation.lat.toFixed(6)},{" "}
                          {endLocation.lng.toFixed(6)}
                        </p>
                        {renderAddress(endLocation.address)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={updateRoutingDetailsMutation.isPending}
          >
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
